import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
    Table,
    TableBody,
    TableContainer,
    TableRow,
    SelectChangeEvent,
    Button,
    Typography,
    Box,
} from "@mui/material";
import { ChevronRight, LocalHospital } from "@mui/icons-material";
import { RootState } from "../../../redux/store";
import { FetchReferalLetterListService } from "../../../utils/services/beneficiary.activity.service";
import { clearModuleRefresh } from "../../../redux/reducer/refreshSlice";
import { Referral } from "../../../utils/dto/module/campaign.activity";
import { openDrawer } from "../../../redux/reducer/drawerSlice";
import PageContent from "../../../components/common/page/PageContent";
import PageHead from "../../../components/common/page/PageHead";
import SearchFilterBar from "../../../components/common/SearchFilterBar";
import TableHeadList from "../../../components/common/table/TableHeadList";
import CommonTableCell from "../../../components/common/table/CommonTableCell";
import { NAVIGATE_ADMIN } from "../../../constant";
import TableSkeleton from "../../../components/loader/TableSkeleton";
import NoDataFound from "../../../components/common/table/NoDataFoundModule";
import CustomPagination from "../../../components/common/table/TablePagination";




const headCells = [
    { id: "sr_no", label: "Sr No", numeric: false, width: "80px", isFilterable: false, isSorting: false },
    { id: "opd_no", label: "Token No", numeric: false, width: "120px", isFilterable: false, isSorting: false },
    { id: "campaign_name", label: "Campaign Name", numeric: false, width: "200px", isFilterable: false, isSorting: false },
    { id: "beneficiary_name", label: "Beneficiary Name", numeric: false, width: "180px", isFilterable: false, isSorting: false },
    { id: "remark", label: "Remark", numeric: false, width: "200px", isFilterable: false, isSorting: false },
    { id: "date", label: "Date", numeric: false, width: "120px", isFilterable: false, isSorting: false },
    { id: "action", label: "Action", numeric: false, width: "120px", isFilterable: false, isSorting: false },
];

const DashboardHospitalReferredPage: React.FC = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { selectedCamp, searchTerm } = useSelector((state: RootState) => state.filters);

    const [loading, setLoading] = useState<boolean>(false);
    const [beneficiaries, setBeneficiaries] = useState<Referral[]>([]);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [payload, setPayload] = useState({
        offset: 0,
        limit: 15,
    });

    const fetchReferalLetterList = useCallback(async () => {

        setLoading(true);
        const reqData = {
            limit: payload.limit,
            offset: payload.offset,
            campaign_uuid: selectedCamp?.uuid || '',
            search: searchTerm,
        };

        const { code, data } = await FetchReferalLetterListService(reqData);

        if (code === 200 && data && data.data) {
            setBeneficiaries(data.data);
            setTotalCount(data.filtered_count || data.total_count || 0);
        } else {
            setBeneficiaries([]);
            setTotalCount(0);
        }
        dispatch(clearModuleRefresh({ moduleName: 'referal' }));
        setLoading(false);
    }, [dispatch, selectedCamp?.uuid, searchTerm, payload.limit, payload.offset]);

    useEffect(() => {
        fetchReferalLetterList();
    }, [fetchReferalLetterList]);

    const handlePageChange = (_event: React.ChangeEvent<unknown>, newPage: number) => {
        setPayload((prev) => ({ ...prev, offset: (newPage - 1) * prev.limit }));
    };

    const handleRowsPerPageChange = (event: SelectChangeEvent) => {
        setPayload((prev) => ({ ...prev, limit: Number(event.target.value), offset: 0 }));
    };

    const handleViewReferalLetter = (user: Referral) => {
        dispatch(openDrawer({
            _Key: 'ViewReferalDetails',
            title: 'View Referral Letter Details',
            drawerWidth: 'lg',
            isFullScreen: false,
            drawerData: user
        }));
    };

    return (
        <PageContent>
            <PageHead
                primary={`Referral List ${selectedCamp ? `- ${selectedCamp.camp_name}` : ''}`}
                back={<LocalHospital />}
            />

            <Box sx={{ backgroundColor: "background.paper", height: "100%", mt: 1 }}>
                {/* <SearchFilterBar totalItems={totalCount} /> */}

                <TableContainer sx={{ minHeight: 'calc(80vh - 70px)', maxHeight: 'calc(80vh - 70px)' }}>
                    <Table stickyHeader size="small" sx={{ minWidth: 750, tableLayout: 'fixed' }}>
                        <TableHeadList headCells={headCells} />
                        <TableBody>
                            {!loading && beneficiaries.map((user, index) => (
                                <TableRow key={user.id || index} hover>
                                    <CommonTableCell style={{ textAlign: 'center' }} value={payload.offset + index + 1} />
                                    <CommonTableCell
                                        style={{ textAlign: 'center' }}
                                        value={
                                            <Typography
                                                onClick={() => handleViewReferalLetter(user)}
                                                sx={{ color: 'blue', fontWeight: 500, cursor: 'pointer' }}
                                            >
                                                {user.token_no ?? 'N/A'}
                                            </Typography>
                                        }
                                    />
                                    <CommonTableCell value={user.campaign_name ?? "--"} />
                                    <CommonTableCell value={user.beneficiary_name ?? "--"} />
                                    <CommonTableCell value={user.remark ?? "--"} />
                                    <CommonTableCell value={user.created_at ?? "--"} formatDate style={{ textAlign: 'center' }} />
                                    <CommonTableCell
                                        value={
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() => navigate(`${NAVIGATE_ADMIN.BENEFICIARY_DETAIL_PAGE}?uuid=${user.beneficiary_uuid}`)}
                                                endIcon={<ChevronRight />}
                                            >
                                                View Profile
                                            </Button>
                                        }
                                        style={{ textAlign: 'center' }}
                                    />
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>

                    {!loading && beneficiaries.length === 0 && (
                        <NoDataFound height="50vh" moduleName="Referral List" />
                    )}

                    {loading && <TableSkeleton columns={headCells.length} rows={15} />}
                </TableContainer>

                <CustomPagination
                    count={totalCount}
                    rowsPerPage={payload.limit}
                    page={payload.offset / payload.limit + 1}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handleRowsPerPageChange}
                    rowsPerPageOptions={[15, 20, 25, 30]}
                />
            </Box>
        </PageContent>
    );
};

export default DashboardHospitalReferredPage;
